import { billingService } from '@/service/console/billing.service';
import type { ApiSingleResponse } from '@/types/api.config';
import type {
  PaymentHistoryResult,
  PaymentStatus,
  ReceiptContent,
  RepaymentContent,
  UsageSummaryContent,
} from '@/types/billing';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-key';

export function useUsageQuery(period: string, organizationId?: string) {
  return useQuery<ApiSingleResponse<UsageSummaryContent>>({
    queryKey: queryKeys.usage(period, organizationId),
    queryFn: () => billingService.getUsage(period, organizationId),
    staleTime: 1000 * 60 * 5,
    enabled: !!period,
  });
}

export function usePaymentHistoryQuery(params: {
  startPeriod: string;
  endPeriod: string;
  paymentStatus?: PaymentStatus;
  page?: number;
  size?: number;
}) {
  return useQuery<{
    header: {
      resultCode: number;
      resultMessage: string;
      isSuccessful: boolean;
    };
    result: PaymentHistoryResult;
  }>({
    queryKey: queryKeys.paymentHistory(
      params.startPeriod,
      params.endPeriod,
      params.paymentStatus,
      params.page,
      params.size
    ),
    queryFn: () => billingService.getPaymentHistory(params),
    staleTime: 1000 * 60 * 5,
    enabled: !!params.startPeriod && !!params.endPeriod,
  });
}

export function useRepaymentMutation() {
  const queryClient = useQueryClient();
  return useMutation<ApiSingleResponse<RepaymentContent>, unknown, string>({
    mutationFn: (paymentNo: string) => billingService.repayment(paymentNo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing });
    },
  });
}

export function useReceiptQuery(paymentNo: string) {
  return useQuery<ApiSingleResponse<ReceiptContent>>({
    queryKey: queryKeys.receipt(paymentNo),
    queryFn: () => billingService.getReceipt(paymentNo),
    enabled: !!paymentNo,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}
